import mapKeys from 'lodash/mapKeys';
import { defineStore } from 'pinia';

import type { Company, SpecialCompany } from '@/features/Companies/types';
import { useGlobalStore } from '@/features/Global/store';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';
import type { ResourceContainer } from '@/types';
import { until } from '@vueuse/core';

interface CompaniesStore extends ResourceContainer<Company[]> {
  data: Company[];
  haveBeenLoaded: boolean;
  hasLoadFailed: boolean;
  specialCompaniesData: SpecialCompany[];
  isLoadingSpecialCompanies: boolean;
}

export const useCompaniesStore = defineStore('CompaniesStore', {
  state: (): CompaniesStore => ({
    isLoading: false,
    haveBeenLoaded: false,
    hasLoadFailed: false,
    data: [],
    specialCompaniesData: [],
    isLoadingSpecialCompanies: false,
  }),

  actions: {
    async loadCompanies() {
      const api = useApi();
      try {
        if (this.haveBeenLoaded) return;

        this.isLoading = true;
        this.hasLoadFailed = false;

        const [, request] = api.companies.loadCompanies();

        const result = await request;

        if (result === null) {
          this.data = [];
        } else {
          this.data = result.map((company) => ({
            ...company,
            insuranceId: company.insuranceId.toString(),
            isBlacklisted: false, // for white label we only have session based blacklists
          }));
        }

        this.haveBeenLoaded = true;
      } catch (error) {
        // Nicht als geladen markieren (erneuter Versuch bleibt möglich), aber den Fehler
        // festhalten, damit Wartende (waitUntilLoaded) nicht endlos hängen
        this.hasLoadFailed = true;

        throw new VglError(VGL_ERROR_CODE.COMPANIES_LOADING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
          cause: error,
        });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Wartet, bis die Gesellschaften geladen sind oder das Laden gescheitert ist.
     * Liefert true bei Erfolg, false bei einem Ladefehler.
     */
    async waitUntilLoaded(): Promise<boolean> {
      await until(() => this.haveBeenLoaded || this.hasLoadFailed).toBeTruthy();

      return this.haveBeenLoaded;
    },

    async loadSpecialCompanies() {
      const api = useApi();
      try {
        this.isLoadingSpecialCompanies = true;

        const [, request] = api.companies.loadSpecialCompanies();

        const result = await request;
        if (result === null) {
          throw new VglError(VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE, {
            additionalErrorMessage: 'companies -> loadSpecialCompanies',
          });
        }

        this.specialCompaniesData = result;
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.COMPANIES_LOADING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
          cause: error,
        });
      } finally {
        this.isLoadingSpecialCompanies = false;
      }
    },

    async setBlacklist(companies: string[]) {
      if (!(await this.waitUntilLoaded())) return;

      companies.forEach((company) => {
        const loadedCompany = this.byInsuranceId[company];
        if (loadedCompany !== undefined) {
          loadedCompany.isBlacklisted = true;
        }
      });
    },

    getFormularcenterLink(id: string) {
      return this.byInsuranceId[id].formularcenterLink ?? null;
    },
  },

  getters: {
    byInsuranceId(state) {
      const { sparte } = useGlobalStore();
      if (!sparte) {
        return {};
      }

      return mapKeys(state.data, ({ insuranceId }) => insuranceId);
    },

    companiesSelection() {
      return (this.filteredList as unknown as Company[]).filter(
        (company) => company.supports.tarife && !company.isBlacklisted,
      );
    },

    blacklistedList(state) {
      return state.data
        .filter((company) => company.isBlacklisted)
        .map((company) => company.insuranceId);
    },

    filteredList(state): Company[] {
      const config = getCurrentSpartenConfig();

      if (config.specifiedCompanyFilter === undefined) {
        return state.data;
      }

      return config.specifiedCompanyFilter();
    },
  },
});
